import { action } from '@ember/object';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { type Task, task } from 'ember-concurrency';
import { pluralize } from 'ember-inflector';
import type FloorModel from 'garaje/models/floor';
import type AjaxService from 'garaje/services/ajax';
import type DesksCsvImporterService from 'garaje/services/desks-csv-importer';
import type FeatureConfigService from 'garaje/services/feature-config';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type MapVersionsService from 'garaje/services/map-versions';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import checkType from 'garaje/utils/check-file-type';
import csvFromArrayOfObjects from 'garaje/utils/object-array-to-csv';

// Type for the checkType function return value
interface CheckTypeResult {
  isValid: boolean;
  extension: string;
}

// Type for desk CSV data
interface DeskCsvData {
  floorName: string;
  neighborhood: string;
  desk: string;
  enabled: string;
  assignedTo: string;
  amenities: string;
}

/**
 *
 */

interface ImportDeskCsvFormArgs {
  file: File | null;
  setCSVFile: (file: File | null) => void;
  records: DeskCsvData[];
  setRecords: (records: DeskCsvData[]) => void;
  floors: FloorModel[];
  onFileSelect: (file: File) => void;
  processing: boolean;
  disabled: boolean;
  hasReachedDesksLimit: boolean;
  uploadFileTask: Task<void, []>;
}

const SampleCSVWithAmenities =
  'Floor Name,Neighborhood,Desk,Enabled,Assigned To,Amenities\n"Required. Can be letters and/or numbers","Optional. Can be the same across different floors","Required. Unique ID of letters and/or numbers","Required. Yes or No. When No, employees won\'t automatically be booked this desk nor can they select it","Optional. Email of person with assigned desk, otherwise leave blank", "Optional. Amenities to apply on the desk."';

export default class ImportDeskCsvForm extends Component<ImportDeskCsvFormArgs> {
  @service declare desksCsvImporter: DesksCsvImporterService;
  @service declare flashMessages: FlashMessagesService;
  @service declare featureConfig: FeatureConfigService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare metrics: MetricsService;
  @service declare state: StateService;
  @service declare store: StoreService;
  @service declare mapVersions: MapVersionsService;
  @service declare ajax: AjaxService;

  @tracked records: DeskCsvData[] = [];

  sampleCSV = SampleCSVWithAmenities;

  @action
  clearAll(): void {
    this.args.setCSVFile(null);
    this.args.setRecords([]);
  }

  /**
   * @param {File}  file Required. The file to upload
   * @returns {void}
   */
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
  parseDesksCsvTask = task(async (file: File): Promise<void> => {
    const { setRecords, setCSVFile, records } = this.args;

    setCSVFile(file);

    const checkResult = checkType(file, ['csv']) as CheckTypeResult;
    if (checkResult.isValid) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      const result = (await this.desksCsvImporter.importRecordsFromCsvFile.perform(file)) as (DeskCsvData | null)[];
      setRecords(result.filter(Boolean) as DeskCsvData[]);
      const wrongRows = result.length - records.length;
      if (wrongRows > 0 && records.length > 0) {
        const message = `${pluralize(wrongRows, 'row')} ${wrongRows > 1 ? 'were' : 'was'} not loaded`;
        this.flashMessages.showFlash(
          'error',
          message,
          'Please check that all rows in your file are properly formatted.',
        );
      }
    } else {
      const text = `Only files with extensions .csv are allowed. You uploaded a file with extension ${checkResult.extension}.`;
      this.flashMessages.showFlash('error', text);
    }
    if (this.args.hasReachedDesksLimit) {
      this.updateRecords();
    }
  });

  @action
  updateRecords(): void {
    let enabledCount = 0;
    const newRecords = this.args.records.map((record) => {
      const enabledStatus = record.enabled.toLowerCase();
      if (enabledStatus === 'yes' || enabledStatus === 'true') {
        enabledCount++;
      }
      const maximumDesksForCurrentPlan = this.featureConfig.getLimit('desks.unlimitedDesks') ?? 0;
      if (enabledCount > maximumDesksForCurrentPlan) {
        record.enabled = 'No';
      }

      return record;
    });
    const newFile = csvFromArrayOfObjects(newRecords, { floorName: 'Floor Name' });
    this.args.setCSVFile(newFile as File);
  }
}
